/**
 * Ribbon action handlers owned by the Pipeline / case-setup workstream.
 *
 * Registration happens once at module import so a ribbon button works even
 * when the Pipeline tab is not mounted.
 */

import {
  createCase,
  deleteCase,
  getSystemStatus,
  listRecentCases,
  loadExampleSubject,
  openCase,
  pickBatchDirectory,
  pickDirectory,
  pickFiles,
  refreshCaseNodes,
  revealPath,
  runBatch,
  saveCase,
  scanSubject,
  splitError,
} from "@/ipc/api";
import type { CaseDraft, CaseManifest, PipelineKind, SubjectScan } from "@/ipc/contract";
import { copyText } from "@/results/clipboard";
import { useResultsStore, type FileSection } from "@/results/state";
import { registerAction } from "@/shell/actions";
import type { TabId } from "@/shell/tabRegistry";
import { useEngineStore } from "@/engine/engineStore";
import { appActions, useAppStore } from "@/store/appStore";
import { useViewerStore } from "@/viewer/viewerStore";
import { editableParams } from "./params";
import { parentDir } from "./paths";
import { fullSelection, modalitiesFor, stageKey, stagesFor } from "./stages";
import { usePipelineSession } from "./session";

export function goToTab(tab: TabId): void {
  appActions.setActiveTab(tab);
}

function fail(error: unknown): void {
  const { message, detail } = splitError(error);
  usePipelineSession.getState().setNotice({ tone: "error", message, detail });
  appActions.notify({ title: message, detail: detail ?? undefined, tone: "error" });
}

function openFileSection(section: FileSection): void {
  useResultsStore.getState().setFileSection(section);
  goToTab("file");
}

function draftFromManifest(manifest: CaseManifest): CaseDraft {
  return {
    subjectDir: `${manifest.inputRoot}/${manifest.subjectRelPath}`,
    outputRoot: manifest.outputRoot,
    subjectId: manifest.subjectId,
    studyName: manifest.studyName,
    pipelineKind: manifest.pipelineKind,
    pwiSubjectDir: manifest.pwiSubjectRelPath
      ? `${manifest.inputRoot}/${manifest.pwiSubjectRelPath}`
      : null,
    gpuIndex: manifest.gpuIndex,
    dockerImage: manifest.dockerImage,
    stages: manifest.stages,
    cleanupEnabled: manifest.cleanupEnabled,
  };
}

async function handleNewCase(kind: PipelineKind = "dwi"): Promise<void> {
  usePipelineSession.getState().startNewAnalysis(kind, "full");
  goToTab("pipeline");
}

function kindFromScan(scan: SubjectScan): PipelineKind {
  if (scan.readiness.combined.ready) return "combined";
  if (scan.readiness.pwi.ready) return "pwi";
  return "dwi";
}

/** Puts the case in the Study Explorer / viewer. Defaults to the View tab. */
export async function showCaseInViewer(
  manifest: CaseManifest,
  opts?: { tab?: TabId },
): Promise<void> {
  let current = manifest;
  if (current.images.length === 0 && current.masks.length === 0) {
    try {
      current = await refreshCaseNodes(current.caseId);
    } catch {
      // Tree stays empty; the user still has Case Setup.
    }
  }
  usePipelineSession.getState().adoptCase(current);
  const first = current.images[0] ?? current.masks[0];
  if (!first) {
    goToTab(opts?.tab ?? "pipeline");
    usePipelineSession.getState().setNotice({
      tone: "error",
      message: "No NIfTI volumes found in that folder.",
      detail: `${current.inputRoot}/${current.subjectRelPath}`,
    });
    appActions.notify({
      title: "No volumes to display",
      detail: "The folder was opened as a case, but it has no NIfTI files the viewer can load.",
      tone: "warning",
    });
    return;
  }
  useViewerStore.getState().setBaseNode(first.id);
  appActions.setSelectedNodeId(first.id);
  goToTab(opts?.tab ?? "view");
  appActions.notify({
    title: `Opened ${current.subjectId}`,
    detail: `${current.images.length} image(s), ${current.masks.length} mask(s)`,
    tone: "success",
  });
}

/**
 * Open a subject folder, a saved case folder, or a case.json path.
 * This is the workstation's main "load data" entry: scan → create case → viewer.
 */
export async function openSubjectOrCase(path: string, opts?: { tab?: TabId }): Promise<void> {
  const asFile = path.toLowerCase().endsWith(".json");
  const caseJson = asFile ? path : `${path.replace(/[\\/]+$/, "")}/case.json`;
  try {
    await showCaseInViewer(await openCase(caseJson), opts);
    return;
  } catch {
    if (asFile) throw new Error(`Could not open ${path} as a case.json.`);
  }

  const scan = await scanSubject(path);
  if (scan.isDatasetRoot) {
    const session = usePipelineSession.getState();
    await session.chooseDatasetRoot(path);
    goToTab("pipeline");
    return;
  }

  const session = usePipelineSession.getState();
  const kind = kindFromScan(scan);
  session.startNewAnalysis(kind, "full");
  await session.chooseSubjectDir(path);
  session.setPipelineKind(kind);
  const manifest = await session.createCaseFromDraft();
  if (!manifest) {
    goToTab(opts?.tab ?? "pipeline");
    return;
  }
  await showCaseInViewer(manifest, opts);
}

export async function importSubjectDir(dir: string): Promise<void> {
  await openSubjectOrCase(dir);
}

export async function pickAndOpenSubject(opts?: { tab?: TabId }): Promise<void> {
  try {
    const prefs = useAppStore.getState().preferences;
    const { path } = await pickDirectory(
      "Open a subject folder, a subjects directory, or a saved case folder",
      prefs?.defaultInputRoot ?? undefined,
    );
    if (!path) return;
    await openSubjectOrCase(path, opts);
  } catch (error) {
    fail(error);
  }
}

/** Loads the real validation subject (sub-02e8eb42) without the native OS
 *  directory picker, so it's reachable by a plain click — by a person or by
 *  browser automation — for local pipeline validation. */
export async function loadExampleAndOpenSubject(opts?: { tab?: TabId }): Promise<void> {
  try {
    const { path } = await loadExampleSubject();
    await openSubjectOrCase(path, opts);
  } catch (error) {
    fail(error);
  }
}

async function handleOpenCase(): Promise<void> {
  await pickAndOpenSubject();
}

async function handleImportData(): Promise<void> {
  await handleOpenCase();
}

async function handleImportNifti(): Promise<void> {
  try {
    const { paths } = await pickFiles("Import NIfTI", ["nii", "gz"], true);
    const first = paths[0];
    if (!first) return;
    await openSubjectOrCase(parentDir(first));
  } catch (error) {
    fail(error);
  }
}

async function handleImportMask(): Promise<void> {
  try {
    const { paths } = await pickFiles("Import mask", ["nii", "gz"], true);
    const first = paths[0];
    if (!first) return;
    if (useAppStore.getState().currentCase) {
      appActions.notify({
        title: "Copy the mask into the subject folder, then Validate Data",
        detail: first,
        tone: "info",
      });
      goToTab("pipeline");
      return;
    }
    await importSubjectDir(parentDir(first));
  } catch (error) {
    fail(error);
  }
}

async function handleOpenOutput(): Promise<void> {
  const manifest = useAppStore.getState().currentCase;
  const { draft } = usePipelineSession.getState();
  const root = manifest ? `${manifest.outputRoot}/${manifest.subjectId}` : draft.outputRoot;
  if (!root) {
    usePipelineSession.getState().setNotice({
      tone: "info",
      message: "No output folder yet — create the case first.",
      detail: null,
    });
    return;
  }
  try {
    await revealPath(root);
  } catch (error) {
    fail(error);
  }
}

async function handleRevealCase(): Promise<void> {
  const manifest = useAppStore.getState().currentCase;
  if (!manifest) return;
  try {
    await revealPath(`${manifest.outputRoot}/${manifest.subjectId}`);
  } catch (error) {
    fail(error);
  }
}

async function handleSaveAs(): Promise<void> {
  try {
    const { path } = await pickDirectory("Save case as — choose an output folder");
    if (!path) return;
    const session = usePipelineSession.getState();
    session.patchDraft({ outputRoot: path });
    const manifest = useAppStore.getState().currentCase;
    if (!manifest) {
      session.setNotice({
        tone: "info",
        message: "Output folder updated. Create Case writes case.json there.",
        detail: null,
      });
      goToTab("pipeline");
      return;
    }
    const updated = { ...manifest, outputRoot: path };
    await saveCase(updated);
    appActions.setCurrentCase(updated);
    session.setNotice({ tone: "info", message: "Case saved to the new output folder.", detail: null });
  } catch (error) {
    fail(error);
  }
}

async function handleOpenRecent(): Promise<void> {
  try {
    const recent = await listRecentCases();
    const first = recent[0];
    if (!first) {
      openFileSection("recent");
      return;
    }
    await showCaseInViewer(await openCase(first.caseJsonPath));
  } catch (error) {
    openFileSection("recent");
    fail(error);
  }
}

async function handleDeleteCase(): Promise<void> {
  const manifest = useAppStore.getState().currentCase;
  if (!manifest) return;
  if (!window.confirm(`Remove ${manifest.subjectId} from Recent Cases? Output files stay on disk.`)) {
    return;
  }
  try {
    await deleteCase(manifest.caseId, false);
    usePipelineSession.getState().closeCase();
    appActions.notify({ title: `Closed ${manifest.subjectId}`, tone: "info" });
    goToTab("home");
  } catch (error) {
    fail(error);
  }
}

async function handleDuplicateCase(): Promise<void> {
  const manifest = useAppStore.getState().currentCase;
  if (!manifest) return;
  try {
    const copy = await createCase({
      ...draftFromManifest(manifest),
      studyName: manifest.studyName ? `${manifest.studyName} copy` : `${manifest.subjectId} copy`,
    });
    usePipelineSession.getState().adoptCase(copy);
    goToTab("pipeline");
  } catch (error) {
    fail(error);
  }
}

async function handleShareCase(): Promise<void> {
  const manifest = useAppStore.getState().currentCase;
  if (!manifest) return;
  const path = `${manifest.outputRoot}/${manifest.subjectId}`;
  const ok = await copyText(path);
  appActions.notify({
    title: ok ? "Case folder path copied" : "Could not copy the path",
    detail: path,
    tone: ok ? "success" : "warning",
  });
}

export const PIPELINE_ACTIONS: Record<string, () => void | Promise<void>> = {
  "case.new": () => handleNewCase(),
  "home.newCase": () => handleNewCase(),
  "case.open": handleOpenCase,
  "home.openCase": handleOpenCase,
  "case.openRecent": handleOpenRecent,
  "import.data": handleImportData,
  "home.importData": handleImportData,
  "import.folder": handleImportData,
  "import.nifti": handleImportNifti,
  "import.mask": handleImportMask,
  "case.save": () => usePipelineSession.getState().saveSetup(),
  "case.saveAs": handleSaveAs,
  "case.close": () => {
    usePipelineSession.getState().closeCase();
    goToTab("home");
  },
  "case.details": () => {
    usePipelineSession.getState().setMode("setup");
    goToTab("pipeline");
  },
  "case.delete": handleDeleteCase,
  "case.duplicate": handleDuplicateCase,
  "case.share": handleShareCase,
  "shell.revealCase": handleRevealCase,

  "job.run": () => usePipelineSession.getState().run(),
  "exec.run": () => usePipelineSession.getState().run(),
  "job.runSelected": () => usePipelineSession.getState().run(),
  "pipeline.runSelected": () => usePipelineSession.getState().run(),
  "job.stop": () => usePipelineSession.getState().stop(),
  "exec.stop": () => usePipelineSession.getState().stop(),
  "job.restartFailed": () => usePipelineSession.getState().restartFailed(),
  "pipeline.restartFailed": () => usePipelineSession.getState().restartFailed(),

  "pipeline.caseSetup": () => {
    usePipelineSession.getState().setMode("setup");
    goToTab("pipeline");
  },
  "pipeline.validate": () => usePipelineSession.getState().rescan(),
  "stages.selectAll": () => usePipelineSession.getState().selectAllStages(),
  "pipeline.selectAllStages": () => usePipelineSession.getState().selectAllStages(),
  "stages.clear": () => usePipelineSession.getState().clearStages(),
  "pipeline.clearStages": () => usePipelineSession.getState().clearStages(),
  "output.open": handleOpenOutput,
  "pipeline.openOutput": handleOpenOutput,
  "output.refresh": () => usePipelineSession.getState().rescan(),
  // These two were the same handler, and neither edited or opened anything: both
  // switched the Pipeline tab to setup mode. "Edit Parameters" now opens the
  // parameters — which are editable where OpenADS reads them from its config —
  // and "Open Config" shows the file itself.
  "config.edit": handleEditParameters,
  "config.open": handleOpenConfig,

  "export.package": () => void useResultsStore.getState().runExport(),
  "export.package.all": () => void useResultsStore.getState().runExport(),
  "export.package.results": () => void useResultsStore.getState().runExport(["csv", "json", "nifti"]),
  "export.package.report": () => void useResultsStore.getState().runExport(["pdf"]),
  "export.image": () => void useResultsStore.getState().runExport(["nifti"]),
  "export.mask": () => void useResultsStore.getState().runExport(["nifti"]),
  "export.table": () => void useResultsStore.getState().runExport(["csv"]),
  "export.report": () => void useResultsStore.getState().runExport(["pdf"]),

  "bookmark.add": () =>
    appActions.notify({
      title: "Bookmarks are not in v1",
      detail: "Use the Study Explorer and Viewer layout instead.",
      tone: "info",
    }),

  "app.preferences.paths": () => openFileSection("preferences"),
  "app.preferences.docker": () => goToTab("developer"),

  "dev.engine.setup": () => {
    goToTab("developer");
    // The engine pane is the first thing on the tab, but the tab may already be
    // open and scrolled elsewhere.
    requestAnimationFrame(() => {
      document.getElementById("engine")?.scrollIntoView({ block: "start" });
    });
  },
  "dev.engine.verify": () => {
    goToTab("developer");
    void useEngineStore.getState().refresh(true);
  },

  "dev.docker.status": () => goToTab("developer"),
  "dev.docker.image": () => goToTab("developer"),
  "dev.docker.pull": () => {
    appActions.notify({
      title: "Pull from a terminal",
      detail: "docker pull vnmd/openadscpu_1.0.0",
      tone: "info",
    });
    goToTab("developer");
  },
  "dev.docker.restart": () => {
    appActions.notify({
      title: "Restart the Docker daemon from the host",
      tone: "info",
    });
    goToTab("developer");
  },
  "dev.gpu.devices": () => goToTab("developer"),
  "dev.gpu.cuda": () => goToTab("developer"),
  "dev.gpu.memory": () => goToTab("developer"),
  "dev.config.yaml": () => goToTab("developer"),
  "dev.pipeline.graph": () => goToTab("pipeline"),
  "dev.pipeline.intermediates": handleOpenOutput,
  "dev.cleanup.toggle": () => {
    const on = useAppStore.getState().toggles["developer.cleanup"] ?? false;
    usePipelineSession.getState().patchDraft({ cleanupEnabled: on });
    void appActions.updatePreferences({ cleanupEnabled: on });
  },
  "dev.log.app": () => {
    appActions.setDockTab("log");
    appActions.togglePanel("bottom", false);
  },
  "dev.log.pipeline": () => {
    appActions.setDockTab("log");
    appActions.togglePanel("bottom", false);
  },
  "dev.log.error": () => {
    appActions.setDockTab("log");
    appActions.togglePanel("bottom", false);
  },
};

let registered = false;

export function registerPipelineActions(): void {
  if (registered) return;
  registered = true;
  for (const [name, handler] of Object.entries(PIPELINE_ACTIONS)) {
    registerAction(name, () => void handler());
  }
}

/**
 * Show the parameters for a step that has some.
 *
 * Landing on an empty Properties panel is what made this button feel broken:
 * the panel only fills once a stage is selected, and nothing selected one.
 */
/**
 * Pick a parent folder of subjects and land on the run form with all of them
 * selected.
 *
 * The cohort entrance used to be a toggle inside the Pipeline tab's setup card,
 * which is not where someone with two hundred subjects looks first.
 * `chooseDatasetRoot` scans the folder, applies the subject filter and ticks
 * everything that matched, so the next click is Run.
 */
/**
 * Real multi-subject batch run, ported from the reference
 * `batch_run_combined.sh`: pick a folder holding one subdirectory per
 * subject, run the real combined DWI+PWI pipeline once per subject,
 * sequentially, continuing past a subject that fails rather than aborting
 * the batch. No native folder picker exists for a rich per-subject
 * readiness review here yet (`chooseDatasetRoot`'s dataset-scan path is
 * still desktop-only) — this runs everything detected directly instead of
 * reviewing first, which is a real, working batch path rather than no path
 * at all.
 */
export async function pickAndOpenCohort(): Promise<void> {
  const { subjectDirs } = await pickBatchDirectory();
  if (subjectDirs.length === 0) {
    appActions.notify({ title: "No subjects found", detail: "That folder had no subject subdirectories.", tone: "warning" });
    return;
  }
  appActions.notify({
    title: `Running ${subjectDirs.length} subject${subjectDirs.length === 1 ? "" : "s"}…`,
    detail: "DWI + PWI, one after another. This will take a while — each subject runs the full real pipeline.",
    tone: "info",
  });
  const result = await runBatch(subjectDirs, {
    pipelineKind: "combined",
    stages: fullSelection("combined"),
    gpuIndex: "0",
    dockerImage: null,
    cleanupEnabled: true,
  });
  appActions.notify({
    title: `Batch finished — ${result.success}/${result.total} succeeded`,
    detail:
      result.failed > 0
        ? `Failed: ${result.subjects.filter((s) => s.state === "failed").map((s) => s.subjectId).join(", ")}`
        : `Took ${Math.round(result.elapsedMs / 60000)} min total.`,
    tone: result.failed > 0 ? "warning" : "info",
  });
  goToTab("output");
}

function handleEditParameters(): void {
  const session = usePipelineSession.getState();
  const kind = useAppStore.getState().currentCase?.pipelineKind ?? session.draft.pipelineKind;
  const chosen = firstStageWithSettableParams(kind);

  appActions.togglePanel("right", false);
  appActions.togglePanel("left", false);
  goToTab(kind === "pwi" ? "perfusion" : "dwi");
  if (chosen) session.selectStage(chosen);
  else {
    appActions.notify({
      title: "No settable parameters",
      detail: "This pipeline's parameters are all fixed in the OpenADS runner.",
      tone: "info",
    });
  }
}

/** The first stage whose parameter list contains something a reader can change. */
function firstStageWithSettableParams(kind: PipelineKind): string | null {
  for (const modality of modalitiesFor(kind)) {
    for (const meta of stagesFor(modality)) {
      if (editableParams(modality, meta.id).length > 0) return stageKey(modality, meta.id);
    }
  }
  return null;
}

/**
 * Open the config the runner will read, in the reader's own editor.
 *
 * Which file that is depends on the engine: the native engine runs the checkout
 * the reader pointed at, so there is a real path to show. Under Docker the
 * config lives inside the image, and saying so is better than revealing a file
 * that has nothing to do with the run.
 */
async function handleOpenConfig(): Promise<void> {
  const session = usePipelineSession.getState();
  const kind = useAppStore.getState().currentCase?.pipelineKind ?? session.draft.pipelineKind;
  const status = await getSystemStatus().catch(() => null);
  const checkout = status?.engine.native?.checkoutPath ?? null;
  if (!checkout) {
    appActions.notify({
      title: "No OpenADS folder to open",
      detail:
        "The config the run reads lives in the OpenADS checkout. Set one in Developer → Engine, or, on Docker, it is inside the image.",
      tone: "warning",
    });
    return;
  }
  const name = kind === "pwi" ? "pwi_pipeline.yaml" : "dwi_pipeline.yaml";
  // defaults.yaml is merged over this one by ads/core/config.py, so the folder
  // is more useful to show than the single file.
  await revealPath(`${checkout}/configs/${name}`);
}
